import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import Mark from '../components/Mark';
import { useT } from '../i18n/i18n';

export default function ResetPasswordPage() {
  const t = useT();
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token') ?? '');
  const key = useRef(token);
  useEffect(() => {
    if (window.location.search) window.history.replaceState(null, '', '/reset-password');
  }, []);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < 12;
  const differs = repeat.length > 0 && repeat !== password;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password.length < 12) { setError(t('Your new password needs at least 12 characters.')); return; }
    if (password !== repeat) { setError(t('The two passwords are not the same.')); return; }
    setError(null);
    setBusy(true);
    try {
      await api<void>('/auth/reset', { method: 'POST', body: { key: key.current, password } });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Your password could not be changed just now. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="reset-heading" style={{ maxWidth: 460, margin: '0 auto' }}>
      <div className="card">
        <h2 id="reset-heading">
          <Mark className="brand-mark" />
          {done ? t('Password changed') : t('Choose a new password')}
        </h2>
        {!key.current ? (
          <>
            <div className="error" role="alert">{t('This link is incomplete. Try opening it again from your email.')}</div>
            <div className="toolbar">
              <Link className="btn" to="/forgot-password">{t('Ask for a new link')}</Link>
            </div>
          </>
        ) : done ? (
          <>
            <p aria-live="polite">{t('Your password has been changed. Sign in with your new password.')}</p>
            <p className="field-hint">{t('Anywhere else you were signed in has been signed out.')}</p>
            <div className="toolbar">
              <Link className="btn" to="/login">{t('Sign in')}</Link>
            </div>
          </>
        ) : (
          <form onSubmit={submit} className="spaced" noValidate>
            {error && (
              <>
                <div className="error" role="alert">{error}</div>
                {error === t('This link has expired or has already been used. Ask for a new one.') && (
                  <div className="toolbar">
                    <Link className="btn secondary" to="/forgot-password">{t('Ask for a new link')}</Link>
                  </div>
                )}
              </>
            )}
            <div className="field">
              <label htmlFor="reset-pw">{t('New password')}</label>
              <input
                id="reset-pw"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={password}
                aria-describedby="reset-pw-hint"
                onChange={(e) => setPassword(e.target.value)}
              />
              <span id="reset-pw-hint" className="field-hint">
                {tooShort ? t('{n} more characters needed.', { n: 12 - password.length }) : t('At least 12 characters, and not the same as your email address.')}
              </span>
            </div>
            <div className="field">
              <label htmlFor="reset-pw2">{t('Type it again')}</label>
              <input
                id="reset-pw2"
                type="password"
                autoComplete="new-password"
                required
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
              />
              {differs && <span className="field-hint">{t('The two passwords are not the same.')}</span>}
            </div>
            <div className="toolbar">
              <button className="btn" type="submit" disabled={busy}>
                {busy ? t('Working…') : t('Change my password')}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
